import { useState, useEffect } from 'react';
import { Card, Button, Space, message, Table, Typography, Select, Row, Col, Tag } from 'antd';
import { DownloadOutlined, FileTextOutlined, CalendarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api, { GeneratedStatement } from '../api';

const { Text } = Typography;
const { Option } = Select;

export function MonthlyStatements() {
  const { t } = useTranslation('settings');
  const [statements, setStatements] = useState<GeneratedStatement[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);

  useEffect(() => {
    fetchStatements();
  }, []);

  const fetchStatements = async () => {
    try {
      setLoading(true);
      const data = await api.statements.listStatements();
      setStatements(data);
    } catch (err: any) {
      console.error('Failed to fetch statements:', err);
      message.error(err.message || t('monthlyStatements.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateStatement = async () => {
    try {
      setGenerating(true);
      await api.statements.createMonthlyStatement({ year: selectedYear, month: selectedMonth });
      message.success(t('monthlyStatements.generateSuccess'));
      await fetchStatements();
    } catch (err: any) {
      console.error('Failed to generate statement:', err);
      message.error(err.message || t('monthlyStatements.generateError'));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (statementId: string, periodStart: string) => {
    try {
      // Open in new tab for GCS signed URLs (avoids CORS issues)
      window.open(`/api/me/statements/${statementId}/download`, '_blank');
    } catch (err: any) {
      console.error('Failed to download statement:', err);
      message.error(err.message || t('monthlyStatements.downloadError'));
    }
  };

  // Generate year options (current year back to 2020)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);

  // Generate month options
  const monthOptions = [
    { value: 1, label: t('monthlyStatements.months.january') },
    { value: 2, label: t('monthlyStatements.months.february') },
    { value: 3, label: t('monthlyStatements.months.march') },
    { value: 4, label: t('monthlyStatements.months.april') },
    { value: 5, label: t('monthlyStatements.months.may') },
    { value: 6, label: t('monthlyStatements.months.june') },
    { value: 7, label: t('monthlyStatements.months.july') },
    { value: 8, label: t('monthlyStatements.months.august') },
    { value: 9, label: t('monthlyStatements.months.september') },
    { value: 10, label: t('monthlyStatements.months.october') },
    { value: 11, label: t('monthlyStatements.months.november') },
    { value: 12, label: t('monthlyStatements.months.december') },
  ];

  // Check if selected month is in the future
  const now = new Date();
  const isSelectedInFuture = 
    selectedYear > now.getFullYear() || 
    (selectedYear === now.getFullYear() && selectedMonth > now.getMonth() + 1);

  const columns = [
    {
      title: t('monthlyStatements.period'),
      dataIndex: 'period_start',
      key: 'period',
      render: (_: any, record: GeneratedStatement) => {
        const startDate = new Date(record.period_start);
        const endDate = new Date(record.period_end);
        return `${startDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} (${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()})`;
      },
    },
    {
      title: t('monthlyStatements.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = 'default';
        let text = status;
        if (status === 'completed') {
          color = 'success';
          text = t('monthlyStatements.statusReady');
        } else if (status === 'failed') {
          color = 'error';
          text = t('monthlyStatements.statusFailed');
        } else if (status === 'pending') {
          color = 'processing';
          text = t('monthlyStatements.statusGenerating');
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: t('monthlyStatements.generated'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: t('monthlyStatements.action'),
      key: 'action',
      render: (_: any, record: GeneratedStatement) => (
        record.status === 'completed' ? (
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record.id, record.period_start)}
          >
            {t('monthlyStatements.downloadButton')}
          </Button>
        ) : record.status === 'failed' ? (
          <Text type="danger">{t('monthlyStatements.generationFailed')}</Text>
        ) : (
          <Text type="secondary">{t('monthlyStatements.generatingText')}</Text>
        )
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>{t('monthlyStatements.title')}</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {t('monthlyStatements.generateNew')}
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {t('monthlyStatements.description')}
            </Text>
            <Row gutter={16} align="middle">
              <Col>
                <Space>
                  <CalendarOutlined />
                  <Select
                    value={selectedMonth}
                    onChange={setSelectedMonth}
                    style={{ width: 140 }}
                  >
                    {monthOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                  <Select
                    value={selectedYear}
                    onChange={setSelectedYear}
                    style={{ width: 100 }}
                  >
                    {yearOptions.map(year => (
                      <Option key={year} value={year}>{year}</Option>
                    ))}
                  </Select>
                </Space>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  loading={generating}
                  disabled={isSelectedInFuture}
                  onClick={handleGenerateStatement}
                >
                  {t('monthlyStatements.generateButton')}
                </Button>
              </Col>
            </Row>
            {isSelectedInFuture && (
              <Text type="warning" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                {t('monthlyStatements.futureMonthWarning')}
              </Text>
            )}
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {t('monthlyStatements.yourStatements')}
            </Text>
            <Table
              dataSource={statements}
              columns={columns}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: t('monthlyStatements.noStatements') }}
            />
          </div>
        </Space>
      </Card>
    </div>
  );
}
